import { AttachedFile } from '../../components/AttachmentsField';
import { DocumentFile } from '../../types/DocumentFile';
import { normalizeFileName } from '../../helpers/commonHelper';

// An attached file as the email dialogs' forms keep it: the saved/new document plus what the attachments field needs to show it.
export type SendEmailFile = DocumentFile & AttachedFile;

// The attachments field needs a stable uid and an errors list on every file; files that come from the server have neither.
export const toAttachedFile = (file: DocumentFile, index: number): SendEmailFile => ({
    ...file,
    uid: file.guidID ? `saved-${file.guidID}` : `saved-${index}-${file.name}`,
    errors: [],
} as SendEmailFile);

// Same rule the old drop zone had: a file whose (normalised) name is already attached is ignored. Only files that are
// new in this change are checked; the rest of the list is returned untouched.
export const dropDuplicateAttachments = (previous: SendEmailFile[], next: AttachedFile[]): AttachedFile[] => {
    const previousUids = new Set(previous.map((file) => file.uid));
    const keptNames = previous.filter((file) => file.status !== 2).map((file) => normalizeFileName(file.name));
    return next.filter((file) => {
        if (previousUids.has(file.uid)) {
            return true;
        }
        const normalizedName = normalizeFileName(file.name);
        if (keptNames.includes(normalizedName)) {
            if (file.preview) {
                URL.revokeObjectURL(file.preview);
            }
            return false;
        }
        keptNames.push(normalizedName);
        return true;
    });
};

// Files the attachments field rejected (wrong size etc.) stay in the list as red rows; they must be removed before sending.
export const validateAttachedFiles = (value?: SendEmailFile[]): string | undefined => {
    const hasInvalidFile = (value || []).some((file) => file.status !== 2 && file.errors && file.errors.length > 0);
    return hasInvalidFile ? 'Remove invalid files before sending.' : undefined;
};

// What is sent to the server: only accepted files (a rejected, red row is never sent).
export const getFilesToSend = (files: SendEmailFile[]) =>
    files
        .filter((document: SendEmailFile) => !document.errors || document.errors.length === 0)
        .map((document: SendEmailFile) => ({
            file: document.file,
            name: document.name,
            guidID: document.guidID,
            status: document.status,
        }));
